import React, { useEffect, useMemo, useState } from "react";
import { getAvailabeCasesAction, AuditCaseReportViewAction, AuditCaseReportDownloadAction } from "../Actions/cases";
import { useSelector } from "react-redux";
import formatDate from "../utils/dateUtil";
import { FileIcon } from "../HemeIconLibrary";
import ResizableTable from "../components/ResizableTable/ResizableTable";
import FilePreviewModal from "../components/FilePreview/FilePreviewModal";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const Reports2 = () => {
  const [cases, setSetCases] = useState([]);
  const { activeSite } = useSelector((state) => state);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filteredData, setFilteredData] = useState(null);

  const [page, setPage] = useState({ first: 0, rows: 10 });
  const [selection, setSelection] = useState(null);

  useEffect(() => {
    fetchCases();
  }, []);
  
  useEffect(() => {
    setFilteredData(cases);
  }, [cases]);

  // Add event listener for site changes
  useEffect(() => {
    const handleSiteChange = (event) => {
      fetchCases();
    };

    window.addEventListener('siteChanged', handleSiteChange);

    return () => {
      window.removeEventListener('siteChanged', handleSiteChange);
    };
  }, []);

  const fetchCases = async () => {
    setLoading(true);
    var result = await getAvailabeCasesAction(
      activeSite || localStorage.getItem("portal-active-site-id")
    );
    if (result?.data) {
      result.data = result?.data?.map((item) => ({ 
        ...item, 
        checked: false,
        status: item.pdfFileUrl ? "Signed Off" : "Accessioned"
      }));
    }
    setSetCases(result?.data);
    setLoading(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "accessionNumber",
        header: "Case Number",
        sortable: true,
        filter: true,
      },
      {
        field: "subjectNumber",
        header: "Subject ID",
        sortable: true,
        filter: true,
      },
      {
        field: "piName",
        header: "Principal Investigator",
        sortable: true,
        filter: true,
      },
      {
        field: "collectedUtc",
        header: "Date Collected",
        sortable: true,
        filter: true,
        body: (row) => formatDate(row.collectedUtc),
      },
      {
        field: "receivedUtc",
        header: "Date Received",
        sortable: true,
        filter: true,
        body: (row) => formatDate(row.receivedUtc),
      },
      {
        field: "reportedUtc",
        header: "Date Reported",
        sortable: true,
        filter: true,
        body: (row) =>
          row.reportedUtc !== "0001-01-01T00:00:00"
            ? formatDate(row.reportedUtc)
            : "",
      },
      {
        field: "sponsorName",
        header: "Sponsor",
        sortable: true,
        filter: true,
      },
      {
        field: "studyName",
        header: "Protocol",
        sortable: true,
        filter: true,
      },
      {
        field: "siteName",
        header: "Site Name",
        sortable: true,
        filter: true,
      },
      {
        field: "siteCode",
        header: "Site Code",
        sortable: true,
        filter: true,
      },
      {
        field: "status",
        header: "Status",
        sortable: true,
        filter: true,
        body: (row) => row.status
      },
      {
        field: "report",
        header: "Report",
        body: (row) =>
          row.pdfFileUrl && (
            <button
              onClick={() => {
                AuditCaseReportViewAction(row);
                setPdfUrl(row.pdfFileUrl);
                setPdfModalOpen(true);
                setSelectedCase(row.accessionNumber);
              }}>
              <FileIcon />
            </button>
          ),
        sortable: false,
        filter: false,
      },
    ],
    []
  );

  const data = useMemo(() => (filteredData ? filteredData : cases) || [], [filteredData, cases]);

  const handleFilter = (event) => {
    const filters = event.filters;
    const dateFields = ['collectedUtc', 'receivedUtc', 'reportedUtc'];

    const filtered = (cases || []).filter(row => {
        return Object.keys(filters).every(field => {
            const filter = filters[field];
            if (filter.value === null || filter.value === undefined || filter.value === '') {
                return true; 
            }

            const value = row[field];
            if (value === null || value === undefined) {
                return false; 
            }
            
            if (dateFields.includes(field)) {
                const formattedDate = formatDate(value);
                return formattedDate.toLowerCase().startsWith(String(filter.value).toLowerCase());
            }

            return String(value).toLowerCase().startsWith(String(filter.value).toLowerCase());
        });
    });
    setFilteredData(filtered);
  };

  const handleDownloadPDFs = async () => {   
    setDownloading(true);
    const zip = new JSZip();
    
    const auditPayload = selection
      .filter(s => s.pdfFileUrl)
      .map(s => ({ id: s.id, accessionNumber: s.accessionNumber, fileUrl: s.pdfFileUrl }));
    if (auditPayload.length) AuditCaseReportDownloadAction(auditPayload);
      
    try {
      const downloadPromises = selection.map(async (caseItem) => {
        if (caseItem.pdfFileUrl) {
          const response = await fetch(caseItem.pdfFileUrl);
          const blob = await response.blob();
          const fileName = `${caseItem.accessionNumber}.pdf`;
          zip.file(fileName, blob);
        }
      });

      await Promise.all(downloadPromises);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "reports.zip");
    } catch (error) {
      console.error('Error downloading PDFs:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleExportCSV = () => {
    setExporting(true);
    try {
      const visibleColumns = columns.filter(col => col.field !== 'report');
      const dataToExport = (selection && selection.length > 0) ? selection : filteredData;
      
      const headers = visibleColumns.map(col => col.header).join(',');
      
      const rows = dataToExport.map(item => {
        return visibleColumns.map(col => {
          let value = item[col.field];
          
          if (col.field === 'collectedUtc' || col.field === 'receivedUtc' || col.field === 'reportedUtc') {
           if (value !== '0001-01-01T00:00:00'){
            value = formatDate(value);      
           }
           else{
            value=''
           }
          }
          
          if (col.field === 'status') {
            value = item.pdfFileUrl ? 'Signed Off' : 'Accessioned';
          }
          
          if (typeof value === 'string') {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          
          return value || '';
        }).join(',');
      });
      
      const csvContent = [headers, ...rows].join('\n');     
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      let filename;
      if (dataToExport.length === 1) {
        filename = `${dataToExport[0].accessionNumber}.csv`;
      } else {
        let baseName = 'CSV_';
        if (selection && selection.length > 0) {
          baseName = `Selected_${selection.length}_items`;
        } else if (filteredData && cases && filteredData.length !== cases.length) {
          baseName = `filtered_${filteredData.length}_items`;
        }
        filename = `${baseName}_${new Date().toISOString().split('T')[0]}.csv`;
      }      
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6 mt-2">
        <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            disabled={(!selection || selection.length === 0) && (!filteredData || filteredData.length === 0)}
            className={`px-3 py-1 border-solid border-2 border-primary1 text-primary1 rounded-md hover:bg-primary1 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {exporting ? 'Exporting...' : 
              selection && selection.length > 0 ? `Export Selected (${selection.length})` :
              (filteredData && cases && filteredData.length !== cases.length) ? `Export Filtered (${filteredData.length})` :
              'Export CSV'
            }
          </button>
          <button
            onClick={handleDownloadPDFs}
            disabled={!selection || selection.length === 0 || downloading}            
            className={`px-3 py-1 border-solid border-2 border-primary1 text-primary1 rounded-md  hover:bg-primary1 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {downloading ? 'Downloading...' : 'Download PDFs'}
          </button>
        </div>
      </div>
      <ResizableTable
        columns={columns}
        data={data}
        loading={loading}
        enableFilters={true}
        selection={selection}
        onSelectionChange={(e) => setSelection(e.value)}
        onFilter={handleFilter}
        page={page}
        onPage={(e) => setPage(e)}
        rows={page.rows}
        totalRecords={data.length}
      />
      <FilePreviewModal
        show={pdfModalOpen}
        onHide={() => {
          setPdfModalOpen(false);
          setSelectedCase(null);
        }}
        fileUrl={pdfUrl}
        caseNumber={selectedCase}
      />
    </>
  );
};

export default Reports2;
